'use server';
import { actionErrorLog } from '@/shared/infrastructure/logging/actionError';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import { getAudioStoryService } from '@/contexts/audio-stories/services/AudioStoryService';

export async function generateBlockAudio(
  audioStoryId: string,
  blockId: string,
  overrides?: { voiceId?: string },
): Promise<ActionResult<void>> {
  const familyId = await SessionService.getFamilyId();
  if (!familyId) return { success: false, error: 'Not authenticated' };
  try {
    const v2 = await revoiceOnPipelineV2(audioStoryId, familyId, blockId, overrides?.voiceId);
    if (v2) return v2;
    await getAudioStoryService().generateBlockAudio(audioStoryId, blockId, familyId, overrides);
    return { success: true, data: undefined };
  } catch (e: unknown) {
    actionErrorLog('generation', e);
    return { success: false, error: e instanceof Error ? e.message : 'Unknown error' };
  }
}

export async function generateBatchAudio(audioStoryId: string): Promise<ActionResult<void>> {
  const familyId = await SessionService.getFamilyId();
  if (!familyId) return { success: false, error: 'Not authenticated' };
  try {
    const { v2AudioOf, v2Failure } = await import('@/contexts/pipeline-v2/executor/legacyEntry.server');
    const v2 = await v2AudioOf(audioStoryId, familyId);
    if (v2) {
      const { getOrderExecutor } = await import('@/contexts/pipeline-v2/executor/server');
      const r = await getOrderExecutor().retryEntity({ storyId: v2.storyId, familyId, kind: 'narration', entityId: audioStoryId });
      return r.ok ? { success: true, data: undefined } : v2Failure(r);
    }
    await getAudioStoryService().generateBatchAudio(audioStoryId, familyId);
    return { success: true, data: undefined };
  } catch (e: unknown) {
    actionErrorLog('generation', e);
    return { success: false, error: e instanceof Error ? e.message : 'Unknown error' };
  }
}

export async function assembleAudio(audioStoryId: string): Promise<ActionResult<void>> {
  const familyId = await SessionService.getFamilyId();
  if (!familyId) return { success: false, error: 'Not authenticated' };
  try {
    const v2 = await revoiceOnPipelineV2(audioStoryId, familyId, null);
    if (v2) return v2;
    await getAudioStoryService().assembleAudio(audioStoryId, familyId);
    return { success: true, data: undefined };
  } catch (e: unknown) {
    actionErrorLog('generation', e);
    return { success: false, error: e instanceof Error ? e.message : 'Unknown error' };
  }
}

/**
 * A narration of the new pipeline is voiced by the new pipeline, in the same model and direction.
 * No block named: only the file is put together again. Null: an older narration, the old service's.
 */
async function revoiceOnPipelineV2(
  audioStoryId: string, familyId: string, blockId: string | null, voiceId?: string,
): Promise<ActionResult<void> | null> {
  const { v2AudioOf, v2Failure } = await import('@/contexts/pipeline-v2/executor/legacyEntry.server');
  const audio = await v2AudioOf(audioStoryId, familyId);
  if (!audio) return null;
  const { EDIT_FORBIDDEN, guardBlockVoice, guardEdit } = await import('@/contexts/pipeline-v2/edits/guardAction.server');
  const { getStoryRepository } = await import('@/contexts/stories/repositories');
  const story = await getStoryRepository().getById(audio.storyId).catch(() => null);
  const blocked = voiceId
    ? await guardBlockVoice({ familyId, storyId: audio.storyId, voiceId, narrationVoiceId: audio.defaultVoiceId ?? null, language: story?.language ?? 'ru' })
    : await guardEdit({ storyId: audio.storyId, kind: 'block_voice' });
  if (blocked) return { success: false, error: blocked.message, code: EDIT_FORBIDDEN };
  const { getOrderExecutor } = await import('@/contexts/pipeline-v2/executor/server');
  const r = await getOrderExecutor().revoice({ storyId: audio.storyId, familyId, audioStoryId, blockId, ...(voiceId ? { voiceId } : {}) });
  return r.ok ? { success: true, data: undefined } : v2Failure(r);
}
